<template>
  <!-- eslint-disable @intlify/vue-i18n/no-raw-text -->
  <template v-if="!showDummyForm">
    <button
      class="dummy-button"
      type="button"
      @click="showDummyForm = true"
    >
      Fill project with dummy data
    </button>
  </template>
  <form
    v-else
    class="dummy-section"
    @submit.prevent="importDummyData"
  >
    <div class="dummy-mode">
      <label class="dummy-radio">
        <input
          v-model="dummyMode"
          :disabled="isImporting"
          name="dummy-mode"
          type="radio"
          value="simple"
        >
        Simple
      </label>
      <label class="dummy-radio">
        <input
          v-model="dummyMode"
          :disabled="isImporting"
          name="dummy-mode"
          type="radio"
          value="advanced"
        >
        Advanced
      </label>
    </div>

    <div class="dummy-amounts">
      <template v-if="dummyMode === 'simple'">
        <label class="dummy-amount-label">
          Amount
          <input
            v-model.number="dummyAmounts.amount"
            class="dummy-amount-input"
            :disabled="isImporting"
            min="0"
            step="1"
            type="number"
          >
        </label>
      </template>
      <template v-else>
        <label
          v-for="field in advancedAmountFields"
          :key="field.key"
          class="dummy-amount-label"
        >
          {{ field.label }}
          <input
            v-model.number="dummyAmounts[field.key]"
            class="dummy-amount-input"
            :disabled="isImporting"
            min="0"
            step="1"
            type="number"
          >
        </label>
      </template>
    </div>

    <div class="row q-gutter-sm q-pt-xs">
      <q-btn
        v-if="!isImporting"
        class="q-px-sm q-py-none no-hover dummy-form-button"
        dense
        label="Cancel"
        no-caps
        outline
        @click="cancelDummyForm"
      />

      <q-btn
        class="q-px-sm q-py-none no-hover dummy-form-button"
        dense
        :label="isImporting ? 'Importing…' : 'Import'"
        :loading="isImporting"
        no-caps
        outline
        type="submit"
      />
    </div>
  </form>
</template>

<script lang="ts" setup>
import {
  reactive,
  ref,
} from 'vue'


const props = defineProps<{
  projectId: string
}>()

const advancedAmountFields = [
  {
    key: 'amount-imports',
    label: 'Imports',
  },
  {
    key: 'amount-inferences',
    label: 'Inferences',
  },
  {
    key: 'amount-models',
    label: 'Models',
  },
  {
    key: 'amount-samples',
    label: 'Samples',
  },
  {
    key: 'amount-tests',
    label: 'Tests',
  },
] as const

const showDummyForm = ref(false)
const dummyMode = ref<'advanced' | 'simple'>('simple')
const isImporting = ref(false)

const defaultAmounts = {
  amount: 25,
  'amount-imports': 0,
  'amount-inferences': 0,
  'amount-models': 0,
  'amount-samples': 0,
  'amount-tests': 0,
} as const

const dummyAmounts = reactive<Record<string, number | undefined>>({ ...defaultAmounts })

function resetDummyAmounts () {
  Object.assign(dummyAmounts, { ...defaultAmounts })
}

function cancelDummyForm () {
  showDummyForm.value = false
  dummyMode.value = 'simple'
  resetDummyAmounts()
}

async function importDummyData () {
  if (isImporting.value) return

  const params = new URLSearchParams()

  if (dummyMode.value === 'simple') {
    if (dummyAmounts.amount != null) {
      params.set('amount', dummyAmounts.amount.toString())
    }
  } else {
    for (const { key } of advancedAmountFields) {
      const value = dummyAmounts[key]
      if (value != null && value > 0) {
        params.set(key, value.toString())
      }
    }
  }
  const query = params.toString() ? `?${params.toString()}` : ''

  isImporting.value = true
  try {
    const response = await fetch(`/hub/api/v1/alpha/projects/${props.projectId}/dummy${query}`, {
      method: 'POST',
    })
    if (!response.ok) {
      throw new Error('Failed to call dummy endpoint')
    }
  } catch (error) {
    console.error('Error calling dummy endpoint:', error)
  } finally {
    isImporting.value = false
    window.location.reload()
  }
}
</script>

<style lang="sass" scoped>
input[type="number"]::-webkit-inner-spin-button,
input[type="number"]::-webkit-outer-spin-button
    -webkit-appearance: none
    margin: 0

input[type="number"]
    -moz-appearance: textfield

.dummy-amount-input
  font-size: 0.75rem
  width: 2.5rem
  border-radius: 5px
  border: 1px solid #000
  margin-left: 5px
  padding: 0 3px

.dummy-button
  appearance: none
  background: none
  border: none
  color: #1976d2
  cursor: pointer
  padding: 0

  &:hover,
  &:focus-visible
    text-decoration: underline

.dummy-section
  border: 1px solid #000
  border-radius: 5px
  padding: 10px
  display: flex
  flex-direction: column
  gap: 6px
  margin-top: 2px

.dummy-mode
  display: flex
  gap: 10px

.dummy-radio
  display: flex
  align-items: center
  gap: 3px
  cursor: pointer

.dummy-amounts
  display: flex
  flex-wrap: wrap
  gap: 4px 10px

.dummy-amount-label
  display: flex
  align-items: center
  gap: 4px
  font-size: 9pt

.dummy-form-button
  font-family: sans-serif
  font-size: 0.8rem
  font-weight: normal

  ::v-deep(.q-btn__content)
    color: black
</style>
